import React, { useState, useCallback } from 'react';
import { Box, Typography, Paper, Tabs, Tab, useTheme } from '@mui/material';
import {
  GridViewRounded as OverviewIcon,
  ManageSearchRounded as KeywordIcon,
  DescriptionRounded as FilingIcon,
  AlternateEmailRounded as TwitterIcon,
} from '@mui/icons-material';

import { useProjectsData } from '../../hooks/useKeywordsData';
import ScraperOverview from './scraping/ScraperOverview';
import KeywordScrapers from './scraping/KeywordScrapers';
import FilingScrapers from './scraping/FilingScrapers';
import TwitterScrapers from './scraping/TwitterScrapers';

const TABS = [
  { value: 'overview', label: 'Scraper Overview', icon: <OverviewIcon /> },
  { value: 'keywords', label: 'Keyword Scrapers', icon: <KeywordIcon /> },
  { value: 'filings', label: 'Filings Scrapers', icon: <FilingIcon /> },
  { value: 'twitter', label: 'Twitter', icon: <TwitterIcon /> },
];

export default function MAB_Scraping() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['overview']));

  const { keywordProjects, filingProjects } = useProjectsData();

  const handleTabChange = useCallback((_e, newValue) => {
    setActiveTab(newValue);
    setVisitedTabs((prev) => {
      if (prev.has(newValue)) return prev;
      return new Set([...prev, newValue]);
    });
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        backgroundColor: theme.palette.grey[50],
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mx: 3,
          mt: 1,
          px: 3,
          pt: 2,
          pb: 0,
          flexShrink: 0,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="h5" fontWeight={600} color="text.primary">
          Scraping Manager
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          Manage scrapers, keywords, and filing pages
        </Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 42,
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              backgroundColor: theme.palette.success.dark,
            },
            '& .MuiTab-root': {
              minHeight: 42,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.85rem',
              color: theme.palette.text.secondary,
              px: 2,
              '&.Mui-selected': {
                fontWeight: 600,
                color: theme.palette.success.dark,
              },
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Box sx={{
          position: 'absolute', inset: 0, overflow: 'auto',
          px: 3, pt: 2, pb: 3,
          display: activeTab === 'overview' ? 'block' : 'none',
        }}>
          {visitedTabs.has('overview') && <ScraperOverview />}
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0, overflow: 'auto',
          px: 3, pt: 2, pb: 3,
          display: activeTab === 'keywords' ? 'block' : 'none',
        }}>
          {visitedTabs.has('keywords') && <KeywordScrapers projects={keywordProjects} />}
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0, overflow: 'auto',
          px: 3, pt: 2, pb: 3,
          display: activeTab === 'filings' ? 'block' : 'none',
        }}>
          {visitedTabs.has('filings') && <FilingScrapers projects={filingProjects} />}
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0, overflow: 'auto',
          px: 3, pt: 2, pb: 3,
          display: activeTab === 'twitter' ? 'block' : 'none',
        }}>
          {visitedTabs.has('twitter') && <TwitterScrapers />}
        </Box>
      </Box>
    </Box>
  );
}
